<script setup lang="ts">
// The left rail: preset buttons + the compare toggle + compare-mode list (port of legacy
// `PresetList.vue`, 86 ln). Preset/compare copy is caller-supplied; the compare toggle reuses the
// shared `Switch` primitive instead of the legacy `o-switch` global.
import { Switch } from '../switch'

import type { CompareMode, CompareModeId, DatePreset, DatePresetId } from './types'

const props = withDefaults(
  defineProps<{
    presets: DatePreset[]
    activePresetId: DatePresetId | null
    compareModes: CompareMode[]
    compareType: CompareModeId
    compareToggleLabel: string
    hideCompare?: boolean
    /** When set, only these preset ids are selectable; the rest render disabled. */
    selectablePresets?: DatePresetId[] | null
    inline?: boolean
    showCustom?: boolean
    disabled?: boolean
  }>(),
  {
    hideCompare: false,
    selectablePresets: null,
    inline: false,
    showCustom: false,
    disabled: false,
  },
)

const compareEnabled = defineModel<boolean>('compareEnabled', { default: false })

const emit = defineEmits<{
  select: [preset: DatePreset]
  'update:compareType': [mode: CompareModeId]
}>()

function isPresetDisabled(id: DatePresetId): boolean {
  return (
    props.disabled || (props.selectablePresets !== null && !props.selectablePresets.includes(id))
  )
}
</script>

<template>
  <div
    class="ui-date-picker__rail flex w-36 flex-col border-r border-border p-xs font-sans"
    :class="{
      'is-inline min-w-64 w-auto flex-row border-r-0': inline,
    }"
  >
    <ul
      class="ui-date-picker__presets m-0 flex list-none flex-col gap-2xs p-0"
      :class="{
        'w-32 border-r border-border pr-xs': inline,
      }"
    >
      <template v-for="preset in presets" :key="preset.id">
        <li v-if="preset.id !== 'Custom' || showCustom">
          <button
            type="button"
            class="ui-date-picker__preset w-full cursor-pointer rounded-md border-0 bg-transparent px-sm py-1.5 text-start text-xs text-fg transition-colors duration-150 hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-40"
            :class="{
              'is-active !bg-(--ui-color-calendar-edge) text-primary-fg hover:bg-info':
                activePresetId === preset.id,
            }"
            :disabled="isPresetDisabled(preset.id)"
            @click="emit('select', preset)"
          >
            {{ preset.label }}
          </button>
        </li>
      </template>
    </ul>

    <div
      v-if="!hideCompare"
      class="ui-date-picker__compare mt-md border-t border-border pt-md"
      :class="{ 'mt-0 w-32 border-t-0 pl-xs pt-0': inline }"
    >
      <div class="ui-date-picker__compare-toggle">
        <Switch v-model="compareEnabled" :label="compareToggleLabel" :disabled="disabled" />
      </div>
      <ul
        v-if="compareEnabled"
        class="ui-date-picker__compare-modes mt-sm flex list-none flex-col gap-2xs p-0"
      >
        <li v-for="mode in compareModes" :key="mode.id">
          <button
            type="button"
            class="ui-date-picker__compare-mode w-full cursor-pointer rounded-md border-0 bg-transparent px-sm py-1.5 text-start text-xs text-fg transition-colors duration-150 hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-40"
            :class="{
              'is-active bg-accent text-primary-fg hover:bg-accent': compareType === mode.id,
            }"
            :disabled="disabled"
            @click="emit('update:compareType', mode.id)"
          >
            {{ mode.label }}
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>
